import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import {
  Car,
  Copy,
  Edit3,
  EyeOff,
  Flame,
  MoreHorizontal,
  Plus,
  Search,
  Filter,
  Archive,
} from "lucide-react";
import { PageHeader } from "@/components/admin/PageHeader";
import { ResourceState } from "@/components/admin/ResourceState";
import { useVehicles } from "@/application/hooks/use-vehicles";
import { formatMiles, formatPrice, uniqueSorted, type Vehicle } from "@/domain/models/vehicle";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";

export function VehiclesPage() {
  const { t, tf } = useI18n();
  const { status, data: items, error, reload, remove, removeMany, duplicate } = useVehicles();
  const [query, setQuery] = useState("");
  const [brand, setBrand] = useState<string>("all");
  const [body, setBody] = useState<string>("all");
  const [availability, setAvailability] = useState<string>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const brands = useMemo(
    () => (items ? uniqueSorted(items.map((v) => v.brand)) : []),
    [items],
  );
  const bodies = useMemo(
    () => (items ? uniqueSorted(items.map((v) => v.body)) : []),
    [items],
  );

  const filtered = useMemo(
    () =>
      (items ?? []).filter((v) => {
        if (query && !`${v.brand} ${v.model}`.toLowerCase().includes(query.toLowerCase()))
          return false;
        if (brand !== "all" && v.brand !== brand) return false;
        if (body !== "all" && v.body !== body) return false;
        if (availability === "available" && !v.available) return false;
        if (availability === "reserved" && v.available) return false;
        if (availability === "featured" && !v.tag) return false;
        return true;
      }),
    [items, query, brand, body, availability],
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    setSelected(
      selected.size === filtered.length ? new Set() : new Set(filtered.map((v) => v.id)),
    );
  };

  const handleDelete = async (id: string) => {
    try {
      await remove(id);
      toast.success(t.admin.vehicles.archived, { description: t.admin.vehicles.archivedDesc });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t.admin.vehicles.archiveFail);
    }
  };

  const handleDuplicate = async (v: Vehicle) => {
    try {
      await duplicate(v);
      toast.success(t.admin.vehicles.duplicated);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t.admin.vehicles.duplicateFail);
    }
  };

  return (
    <div>
      <PageHeader
        title={t.admin.vehicles.title}
        description={t.admin.vehicles.description}
        actions={
          <Link
            to="/admin/vehicles/new"
            className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-xs font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition hover:-translate-y-0.5"
          >
            <Plus className="h-3.5 w-3.5" /> {t.admin.vehicles.newVehicle}
          </Link>
        }
      />

      <ResourceState
        status={status}
        error={error}
        onRetry={() => void reload()}
        emptyMessage={t.admin.vehicles.empty}
      >
        <div className="mb-4 grid grid-cols-1 gap-2 rounded-2xl border border-border/60 bg-surface-elevated/60 p-3 backdrop-blur sm:grid-cols-[1fr_auto_auto_auto_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t.admin.vehicles.search}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-9 border-border/60 bg-background/60 pl-9"
            />
          </div>
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="h-9 w-full sm:w-[160px]">
              <SelectValue placeholder={t.admin.vehicles.brand} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.admin.vehicles.allBrands}</SelectItem>
              {brands.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={body} onValueChange={setBody}>
            <SelectTrigger className="h-9 w-full sm:w-[140px]">
              <SelectValue placeholder={t.admin.vehicles.body} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.admin.vehicles.allBodies}</SelectItem>
              {bodies.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={availability} onValueChange={setAvailability}>
            <SelectTrigger className="h-9 w-full sm:w-[140px]">
              <SelectValue placeholder={t.admin.vehicles.status} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.admin.vehicles.allStatuses}</SelectItem>
              <SelectItem value="available">{t.admin.common.available}</SelectItem>
              <SelectItem value="reserved">{t.admin.common.reserved}</SelectItem>
              <SelectItem value="featured">{t.admin.common.featured}</SelectItem>
            </SelectContent>
          </Select>
          <button className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border/60 bg-background/60 px-3 text-xs font-medium text-muted-foreground hover:text-foreground">
            <Filter className="h-3.5 w-3.5" /> {t.admin.common.more}
          </button>
        </div>

        {selected.size > 0 && (
          <div className="mb-3 flex items-center justify-between rounded-xl border border-primary/40 bg-primary/10 px-4 py-2.5 text-sm animate-fade-in">
            <span className="font-medium">{tf(t.admin.vehicles.selected, { count: selected.size })}</span>
            <div className="flex items-center gap-2">
              <button className="rounded-md px-2.5 py-1 text-xs font-medium hover:bg-primary/20">
                {t.admin.vehicles.feature}
              </button>
              <button className="rounded-md px-2.5 py-1 text-xs font-medium hover:bg-primary/20">
                {t.admin.vehicles.unpublish}
              </button>
              <button
                onClick={() => {
                  void (async () => {
                    try {
                      await removeMany([...selected]);
                      setSelected(new Set());
                      toast.success(t.admin.vehicles.archivedMany);
                    } catch (err) {
                      toast.error(err instanceof Error ? err.message : t.admin.vehicles.archiveManyFail);
                    }
                  })();
                }}
                className="rounded-md bg-destructive/20 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/30"
              >
                {t.admin.vehicles.archive}
              </button>
            </div>
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-border/60 bg-surface-elevated/60 backdrop-blur">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border/60 bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <Checkbox
                      checked={selected.size > 0 && selected.size === filtered.length}
                      onCheckedChange={toggleAll}
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-medium">{t.admin.vehicles.colVehicle}</th>
                  <th className="hidden px-4 py-3 text-left font-medium md:table-cell">{t.admin.vehicles.colStock}</th>
                  <th className="hidden px-4 py-3 text-left font-medium lg:table-cell">{t.admin.vehicles.colMileage}</th>
                  <th className="px-4 py-3 text-left font-medium">{t.admin.vehicles.colPrice}</th>
                  <th className="px-4 py-3 text-left font-medium">{t.admin.vehicles.colStatus}</th>
                  <th className="hidden px-4 py-3 text-left font-medium xl:table-cell">{t.admin.vehicles.colViews}</th>
                  <th className="w-12 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {filtered.map((v, i) => (
                  <tr key={v.id} className="group transition hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selected.has(v.id)}
                        onCheckedChange={() => toggle(v.id)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={v.image}
                          alt=""
                          className="h-11 w-16 shrink-0 rounded-md object-cover ring-1 ring-border/60"
                        />
                        <div className="min-w-0">
                          <div className="truncate font-semibold">
                            {v.brand} {v.model}
                          </div>
                          <div className="truncate text-[11px] text-muted-foreground">
                            {v.year} · {v.body} · {v.color}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 font-mono text-xs text-muted-foreground md:table-cell">
                      MRD-{String(1200 + i)}
                    </td>
                    <td className="hidden px-4 py-3 tabular-nums text-muted-foreground lg:table-cell">
                      {formatMiles(v.mileage)}
                    </td>
                    <td className="px-4 py-3 font-semibold tabular-nums text-primary">
                      {formatPrice(v.price)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                            v.available
                              ? "bg-emerald-500/15 text-emerald-400"
                              : "bg-amber-500/15 text-amber-400",
                          )}
                        >
                          <span
                            className={cn(
                              "h-1.5 w-1.5 rounded-full",
                              v.available ? "bg-emerald-400" : "bg-amber-400",
                            )}
                          />
                          {v.available ? t.admin.common.available : t.admin.common.reserved}
                        </span>
                        {v.tag && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                            <Flame className="h-2.5 w-2.5" /> {v.tag}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 tabular-nums text-muted-foreground xl:table-cell">
                      {(2400 - i * 180).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground opacity-60 transition hover:bg-muted hover:text-foreground group-hover:opacity-100">
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/vehicles/${v.id}`}>
                              <Edit3 className="h-3.5 w-3.5" /> {t.admin.common.edit}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => void handleDuplicate(v)}>
                            <Copy className="h-3.5 w-3.5" /> {t.admin.common.duplicate}
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Flame className="h-3.5 w-3.5" /> {v.tag ? t.admin.common.unfeature : t.admin.common.feature}
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <EyeOff className="h-3.5 w-3.5" /> {t.admin.common.unpublish}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => void handleDelete(v.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Archive className="h-3.5 w-3.5" /> {t.admin.common.archive}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-16 text-center">
                      <Car className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">{t.admin.vehicles.noMatch}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col items-center justify-between gap-2 border-t border-border/60 px-4 py-3 text-xs text-muted-foreground sm:flex-row">
            <div>
              {tf(t.admin.vehicles.showing, { filtered: filtered.length, total: items?.length ?? 0 })}
            </div>
            <div className="flex items-center gap-1">
              <button className="rounded-md border border-border/60 px-2 py-1 hover:bg-muted">
                {t.admin.common.previous}
              </button>
              <button className="rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-primary">
                1
              </button>
              <button className="rounded-md border border-border/60 px-2 py-1 hover:bg-muted">
                {t.admin.common.next}
              </button>
            </div>
          </div>
        </div>
      </ResourceState>
    </div>
  );
}
